import React from "react";
import { EXCHANGE_LABELS, getExchangeLogo } from "@cede/utils";
import Image from "next/image";

export const highlightTokens = (text: string) => {
  const regex = /(\$[A-Z]{2,10}|\d+%|\$\d[\d,]*\.?\d*)/g;
  return text.split(regex).map((part, index) => {
    if (!part) return null;
    const key = `${part}-${index}`;
    if (part.match(/^\$[A-Z]/)) {
      return (
        <span
          key={key}
          className="inline-flex items-center rounded-full bg-cede-bg-component/60 px-2 py-0.5 text-cede-text-accent font-semibold text-[0.9em] tracking-tight"
        >
          {part}
        </span>
      );
    }
    if (part.match(/^\d+%|^\$\d/)) {
      return (
        <span
          key={key}
          className="inline-flex items-center rounded bg-cede-bg-component/50 px-1.5 py-0.5 text-cede-text-accent font-semibold text-[0.9em]"
        >
          {part}
        </span>
      );
    }
    return part;
  });
};

export const renderHighlighted = (children: React.ReactNode) =>
  React.Children.toArray(children).map((child) => (typeof child === "string" ? highlightTokens(child) : child));

// Create a reverse mapping from exchange labels to exchange IDs
const EXCHANGE_NAME_TO_ID: Record<string, string> = Object.entries(EXCHANGE_LABELS).reduce(
  (acc, [id, label]) => {
    acc[label.toLowerCase()] = id;
    return acc;
  },
  {} as Record<string, string>,
);

export const highlightExchangeInTitle = (title: string) => {
  // Try to find any exchange name in the title (case-insensitive)
  for (const [exchangeName, exchangeId] of Object.entries(EXCHANGE_NAME_TO_ID)) {
    const regex = new RegExp(`\\b(${exchangeName})\\b`, "i");
    const match = title.match(regex);

    if (match) {
      const matchedText = match[1];
      const parts = title.split(regex);
      const logo = getExchangeLogo(exchangeId);

      return parts.map((part) => {
        if (part.toLowerCase() === exchangeName) {
          return (
            <span
              key={`exchange-${exchangeId}`}
              className="flex md:inline-flex items-center gap-2 mb-2 md:mb-0 justify-center md:justify-start"
            >
              {logo && (
                <Image
                  src={logo}
                  alt={matchedText ?? ""}
                  width={24}
                  height={24}
                  className="inline-block w-6 h-6 md:w-7 md:h-7 shrink-0"
                />
              )}
              <span className="text-cede-text-accent font-bold text-2xl md:text-4xl">{matchedText}</span>
            </span>
          );
        }
        return part;
      });
    }
  }

  return title;
};
